import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Alert,
  TextField,
} from "@mui/material";

import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { formatPhoneNumber } from "../../utils/functions";
import AvatarUpload from "../shared/AvatarUpload";

const ChangePasswordSection = ({ api }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const handleSubmit = async () => {
    setAlert(null);
    if (newPassword.length < 8) {
      setAlert({ type: "error", message: "New password must be at least 8 characters." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setAlert({ type: "error", message: "Passwords do not match." });
      return;
    }
    setLoading(true);
    try {
      await api("/auth/change-password.php", {
        method: "POST",
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword }),
      });
      setAlert({ type: "success", message: "Password updated successfully." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      setAlert({ type: "error", message: e.message || "Failed to update password." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Change Password
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {alert && (
          <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ fontSize: 12, borderRadius: 1.5 }}>
            {alert.message}
          </Alert>
        )}
        <TextField
          type="password"
          size="small"
          placeholder="Current password"
          value={currentPassword}
          disabled={loading}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          placeholder="New password"
          value={newPassword}
          disabled={loading}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          placeholder="Confirm new password"
          value={confirmPassword}
          disabled={loading}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleSubmit}
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            sx={{
              backgroundColor: "#111008",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 1.5,
              px: 2,
              "&:hover": { backgroundColor: "#1e1c14" },
              "&.Mui-disabled": { backgroundColor: "rgba(0,0,0,0.15)", color: "rgba(0,0,0,0.4)" },
            }}
          >
            {loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Update Password"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const AdminProfile = () => {
  const { user, setUser } = useAuth();
  const api = useApi();

  return (
    <Box sx={{ p: { xs: 3, md: 5 }, maxWidth: 640 }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Profile
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Manage your account details.
      </Typography>

      <AvatarUpload user={user} api={api} setUser={setUser} />

      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Account
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {[
          {
            label: "Name",
            value: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
          },
          { label: "Email", value: user?.email ?? "—" },
          {
            label: "Phone",
            value: user?.phone_number ? formatPhoneNumber(user.phone_number) : "—",
          },
          {
            label: "Role",
            value: user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "—",
          },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 80 }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 4 }} />

      <ChangePasswordSection api={api} />
    </Box>
  );
};

export default AdminProfile;
